/**
 * Button. SPEC.md §19, §18.4, §36.
 *
 * A real <button> with a real pressed state, a focus-visible ring and a minimum
 * 48 px target. `selected` maps to aria-pressed, so a chosen option is announced
 * as pressed rather than only looking different. §19
 *
 * The press animation is local and immediate; it never waits on the network,
 * which is the whole of the optimistic story here — no total is ever moved ahead
 * of the server. §36
 */

import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg';

/** Shared by Button and IconButton so the two can never look like siblings. */
export const buttonBaseClasses = [
  'relative inline-flex items-center justify-center gap-2 rounded-control font-semibold',
  'transition-[transform,opacity,filter] duration-150 ease-out-soft',
  'active:scale-[0.97] disabled:pointer-events-none disabled:opacity-45',
].join(' ');

export const buttonVariantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-ink hover:brightness-110',
  secondary: 'bg-raised text-ink border border-line hover:border-ink-faint',
  ghost: 'bg-transparent text-ink-muted hover:text-ink',
  danger: 'bg-negative-soft text-negative border border-negative/50',
};

export const buttonSizeClasses: Record<ButtonSize, string> = {
  sm: 'min-h-tap px-3 text-sm',
  md: 'min-h-tap px-4 text-base',
  lg: 'min-h-[56px] px-5 text-lg',
};

/** Selected controls get a ring as well as a fill, per §19 and §20.3. */
export const buttonSelectedClasses = 'ring-2 ring-accent ring-offset-2 ring-offset-canvas';

export interface ButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Shows a spinner and blocks input without collapsing the layout. */
  loading?: boolean;
  /** Toggle state for chips and mode pickers; sets aria-pressed. §19 */
  selected?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  selected,
  type = 'button',
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    buttonBaseClasses,
    buttonVariantClasses[variant],
    buttonSizeClasses[size],
    fullWidth ? 'w-full' : '',
    selected ? buttonSelectedClasses : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      type={type}
      disabled={disabled === true || loading}
      aria-pressed={selected}
      aria-busy={loading || undefined}
      className={classes}
    >
      {loading ? <Spinner size="sm" label="Working…" /> : null}
      {children}
    </button>
  );
}
